import type { ProfileTeamData } from "@qendo/profile";
import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { FieldGroup } from "~/components/form/FieldGroup";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { BodyText, Title } from "~/components/layout/text";
import { Nothing } from "~/components/permission/Nothing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useSession } from "~/providers/SessionProvider";
import { useUser } from "~/providers/UserProvider";

export type LeaveTeamProps = {
    team: ProfileTeamData;
    onRequestClose: () => void;
} & FullPageModalProps;

export const LeaveTeam = ({ team, onRequestClose, ...modalProps }: LeaveTeamProps) => {
    const { profile_id, changeProfile } = useProfile();
    const { isPrimaryTeam, leaveTeam } = useUser();
    const { is_share_user } = useSession();
    const { loading, setLoading, addToast } = useFeedback();

    const [reason, setReason] = useState("");
    const [remain, setRemain] = useState(true);
    const [confirm, setConfirm] = useState(false);

    const primary = isPrimaryTeam(team.profile_id);
    const can_leave = !primary && !is_share_user;
    const is_emergency_contact = team.emergency_contact;

    useEffect(() => {
        setReason("");
        setRemain(true);
        setConfirm(false);
    }, []);

    if (!can_leave) {
        setTimeout(onRequestClose, 10);

        return <Nothing />;
    }

    const leave = () => {
        if (loading || !confirm) {
            return;
        }

        setLoading(true);

        leaveTeam({
            id: team.id,
            reason,
            remain_contact: is_emergency_contact && remain,
            confirm,
        })
            .then(() => {
                addToast({ content: `Left ${team.name}'s care team.` });

                if (profile_id === team.profile_id) {
                    changeProfile();
                }

                onRequestClose();
            })
            .finally(() => setLoading(false));
    };

    return (
        <FullPageModal {...modalProps} title="Leave care team" cancelable onRequestClose={onRequestClose}>
            <Title>Are you sure you want to leave {team.name}&#39;s care team?</Title>
            <BodyText>
                If you leave you will no longer be able to access their profile information, however they can invite you
                to their team again later. Your own profile will remain unaffected.
            </BodyText>
            <FieldGroup>
                <TextArea name="reason" label="Reason for leaving" value={reason} onChangeText={setReason} optional />
                {is_emergency_contact && (
                    <CheckboxInput
                        label="I wish to remain as an emergency contact."
                        value={remain}
                        onValueChange={setRemain}
                    />
                )}
                <CheckboxInput
                    label="I confirm that I wish to leave this team."
                    value={confirm}
                    onValueChange={setConfirm}
                    required
                />
            </FieldGroup>
            <Button
                size="lg"
                variant="destructive"
                disabled={!confirm}
                loading={loading}
                icon="trash"
                title="Leave care team"
                onPress={leave}
            />
        </FullPageModal>
    );
};
